import { CircularProgress, Grid, Paper } from "@material-ui/core";
import { getDate, isRoleViewSensitiveImage } from "app/common/LocalFunction";
import { SENSITIVE_PLACEHOLDER_IMAGE } from "app/common/LocalConst";
import { useStore } from "app/stores";
import LightGallery from "lightgallery/react";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import lgFullscreen from "lightgallery/plugins/fullscreen";
import lgRotate from "lightgallery/plugins/rotate";
import { observer } from "mobx-react";
import React, { memo, useEffect, useState, useRef } from "react";
import { FixedSizeList as List } from "react-window";

import Pagination from "app/common/Component/Table/Pagination";

const LOADING_PLACEHOLDER_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"; // 1x1 Transparent GIF

const ImageLoaderBase = ({
  fileId,
  fetchImage,
  getImageUrl,
  name,
  isShowSensitive,
}) => {
  const [url, setUrl] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observerObj = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observerObj.disconnect();
      }
    }, { threshold: 0.1 });
    if (containerRef.current) observerObj.observe(containerRef.current);
    return () => observerObj.disconnect();
  }, [fileId]);

  useEffect(() => {
    if (!isVisible) return;
    const controller = new AbortController();
    if (isShowSensitive) {
      setUrl(SENSITIVE_PLACEHOLDER_IMAGE);
      return;
    }
    const fetchAndSet = async () => {
      try {
        const imageUrl = await fetchImage(fileId, controller.signal);
        if (imageUrl) setUrl(imageUrl);
      } catch (e) {}
    };
    fetchAndSet();
    return () => controller.abort();
  }, [fileId, fetchImage, isShowSensitive, isVisible]);

  // Priority: 1. Sensitive Mask, 2. Loaded URL, 3. Empty Placeholder
  const displayUrl = isShowSensitive ? SENSITIVE_PLACEHOLDER_IMAGE : (url || LOADING_PLACEHOLDER_IMAGE);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", aspectRatio: "1/1", padding: 2 }}>
      <img
        src={displayUrl}
        alt={name}
        loading="lazy"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: 4,
          backgroundColor: "#f5f5f5",
          opacity: (url || isShowSensitive) ? 1 : 0.4,
          transition: "opacity 0.3s ease"
        }}
      />
    </div>
  );
};
export const ImageLoader = memo(observer(ImageLoaderBase));

const TreamentImagePatient = () => {
  const {
    pagingTreatments,
    listImageTreatment,
    fetchImage,
    getImageUrl,
    totalElements,
    totalPages,
    searchImageTreatmentPlan,
    setPageIndex,
    setPageSize,
    isLoading,
  } = useStore().treatmentManagementStore.treamentInfoPatientStore;

  const patientId = new URLSearchParams(window.location.search).get("patientId");
  const isViewSensitive = isRoleViewSensitiveImage();
  const requestIdRef = useRef(0);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    if (patientId) {
      pagingTreatments(patientId).then(() => {
        if (currentRequestId !== requestIdRef.current) console.warn("Outdated request ignored");
      });
    }
  }, [patientId, searchImageTreatmentPlan.pageIndex, searchImageTreatmentPlan.pageSize]);

  if (isLoading) return <div className="text-center p-5"><CircularProgress size={40} /></div>;

  return (
    <div className="mx-1">
      <Grid container spacing={1}>
        {listImageTreatment?.length > 0 ? (
          listImageTreatment.map((item) => {
            const date = getDate(item?.orderDate);
            const serviceName = item?.service?.name || "Dịch vụ";
            const isShowSensitive = item?.service?.isSensitive && !isViewSensitive;
            const imagesBefore = item?.diagnosticReport?.imagesBeforeTreatment || [];
            const imagesAfter = item?.diagnosticReport?.imagesAfterTreatment || [];
            
            return (
              <Grid item xs={12} key={item.id}>
                <Paper elevation={1} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #f0f0f0", background: "#fff", marginBottom: "2px" }}>
                  <div style={{ display: "flex", gap: "15px", borderBottom: "1px dashed #eee", paddingBottom: "6px", marginBottom: "8px", fontSize: "13px" }}>
                    <div><b>Ngày:</b> {date}</div>
                    <div><b>Dịch vụ:</b> {serviceName}</div>
                  </div>
                  
                  <Grid container spacing={1}>
                    {imagesBefore.length > 0 && (
                      <Grid item xs={imagesAfter.length > 0 ? 6 : 12}>
                        <div style={{ fontWeight: "600", marginBottom: "4px", fontSize: "12px", color: "#666" }}>Trước điều trị</div>
                        <LightGalleryInstance 
                          id={`${item.id}-before`} 
                          images={imagesBefore} 
                          fetchImage={fetchImage} 
                          getImageUrl={getImageUrl} 
                          isShowSensitive={isShowSensitive} 
                        />
                      </Grid>
                    )}
                    {imagesAfter.length > 0 && (
                      <Grid item xs={imagesBefore.length > 0 ? 6 : 12}>
                        <div style={{ fontWeight: "600", marginBottom: "4px", fontSize: "12px", color: "#666" }}>Sau điều trị</div>
                        <LightGalleryInstance 
                          id={`${item.id}-after`} 
                          images={imagesAfter} 
                          fetchImage={fetchImage} 
                          getImageUrl={getImageUrl} 
                          isShowSensitive={isShowSensitive} 
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            );
          })
        ) : <div className="text-center p-4 text-gray-400">Không có dữ liệu hình ảnh</div>}
      </Grid>
      <div className="mt-2">
        <Pagination
          totalPages={totalPages}
          pageIndex={searchImageTreatmentPlan.pageIndex}
          pageSize={searchImageTreatmentPlan.pageSize}
          totalElements={totalElements}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
      </div>
    </div>
  );
};

// Subcomponent to handle LightGallery instance and refreshing (Fixes Thumbnails)
const LightGalleryInstance = observer(({ images, fetchImage, getImageUrl, isShowSensitive }) => {
  const lgRef = useRef(null);
  const onInit = (detail) => { lgRef.current = detail.instance; };

  const { imageUrls } = useStore().treatmentManagementStore.treamentInfoPatientStore;
  
  // All images in this specific group must be processed (either URL or FAILED/SENSITIVE)
  const isAllProcessed = images.every(img => {
    const id = img?.fileDescription?.id;
    return isShowSensitive || !!imageUrls[id];
  });

  useEffect(() => {
    if (lgRef.current && isAllProcessed) {
      const timeout = setTimeout(() => {
        lgRef.current.refresh();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [isAllProcessed, images.length]);

  const GalleryContent = (
    <>
      {images.map((img) => {
        const fileId = img?.fileDescription?.id;
        const blobUrl = getImageUrl(fileId);
        const finalSrc = isShowSensitive ? SENSITIVE_PLACEHOLDER_IMAGE : (blobUrl || LOADING_PLACEHOLDER_IMAGE);
        
        return (
          <div 
            key={img.id} 
            data-src={finalSrc}
            data-thumb={finalSrc}
            className="lightgallery-item"
            onMouseEnter={() => !blobUrl && !isShowSensitive && fetchImage(fileId)}
            style={{ 
              width: "100px", 
              height: "100px", 
              display: "inline-block", 
              margin: "2px", 
              cursor: isAllProcessed ? "pointer" : "wait",
              opacity: isAllProcessed ? 1 : 0.7
            }}
          >
            <ImageLoader 
              fileId={fileId}
              fetchImage={fetchImage} 
              getImageUrl={getImageUrl} 
              isShowSensitive={isShowSensitive} 
            />
          </div>
        );
      })}
    </>
  );

  if (!isAllProcessed) {
    return (
      <div style={{ position: "relative", pointerEvents: "none", minHeight: "100px" }}>
        {GalleryContent}
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(255,255,255,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "12px", color: "#888", fontWeight: "bold",
          zIndex: 10
        }}>
          ...
        </div>
      </div>
    );
  }

  return (
    <LightGallery 
      onInit={onInit}
      selector=".lightgallery-item"
      plugins={[lgThumbnail, lgZoom, lgFullscreen, lgRotate]} 
      mode="lg-fade"
    >
      {GalleryContent}
    </LightGallery>
  );
});

export default memo(observer(TreamentImagePatient));
